// src/context/ChatContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { db, storage } from '../firebase';
import { 
  collection, query, where, orderBy, limit, onSnapshot, 
  doc, getDoc, updateDoc, arrayUnion, arrayRemove,
  addDoc, serverTimestamp, setDoc, getDocs, deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const ChatContext = createContext();
export const useChat = () => useContext(ChatContext);

// ==================== CHAT PROVIDER COMPONENT ====================

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState({});
  const [typingUsers, setTypingUsers] = useState([]);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  
  const typingTimeouts = useRef({});
  const isMounted = useRef(true);
  
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // ===== SEND MESSAGE (NO ENCRYPTION) =====
  const sendMessage = async (chatId, text) => {
    if (!user || !text.trim()) return;
    
    const chat = chats.find(c => c.id === chatId);
    if (!chat) return;
    
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    await addDoc(messagesRef, {
      senderId: user.uid,
      text: text.trim(),
      timestamp: serverTimestamp(),
      readBy: [user.uid],
    });
    
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    if (chatSnap.exists()) {
      const participants = chatSnap.data().participants;
      const updates = {
        lastMessage: text.trim(),
        lastUpdated: serverTimestamp(),
      };
      participants.forEach(uid => {
        if (uid !== user.uid) {
          updates[`unreadCounts.${uid}`] = (chatSnap.data().unreadCounts?.[uid] || 0) + 1;
        }
      });
      await updateDoc(chatRef, updates);
    }
  };
  
  // ===== SEND IMAGE =====
  const sendImage = async (chatId, file) => {
    if (!user || !file) return;
    try {
      const storageRef = ref(storage, `chat_images/${chatId}/${Date.now()}_${file.name}`);
      await uploadBytesResumable(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);
      
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      await addDoc(messagesRef, {
        senderId: user.uid,
        imageUrl,
        text: '',
        timestamp: serverTimestamp(),
        readBy: [user.uid],
      });
      
      const chatRef = doc(db, 'chats', chatId);
      const chatSnap = await getDoc(chatRef);
      if (chatSnap.exists()) {
        const participants = chatSnap.data().participants;
        const updates = { lastMessage: '📷 Image', lastUpdated: serverTimestamp() };
        participants.forEach(uid => {
          if (uid !== user.uid) {
            updates[`unreadCounts.${uid}`] = (chatSnap.data().unreadCounts?.[uid] || 0) + 1;
          }
        });
        await updateDoc(chatRef, updates);
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      throw err;
    }
  };
  
  // ===== TYPING INDICATORS =====
  const startTyping = (chatId) => {
    if (!user) return;
    if (typingTimeouts.current[chatId]) clearTimeout(typingTimeouts.current[chatId]);
    const typingDoc = doc(db, 'chats', chatId, 'typing', user.uid);
    setDoc(typingDoc, { timestamp: serverTimestamp() }).catch(console.error);
    typingTimeouts.current[chatId] = setTimeout(() => stopTyping(chatId), 2000);
  };
  
  const stopTyping = (chatId) => {
    if (!user) return;
    if (typingTimeouts.current[chatId]) clearTimeout(typingTimeouts.current[chatId]);
    const typingDoc = doc(db, 'chats', chatId, 'typing', user.uid);
    deleteDoc(typingDoc).catch(console.error);
  };
  
  // ===== CREATE NEW CHAT =====
  const createPrivateChat = async (otherUserId) => {
    if (!user) return null;
    
    const existing = chats.find(chat =>
      chat.type === 'private' &&
      chat.participants.includes(otherUserId) &&
      chat.participants.includes(user.uid)
    );
    if (existing) {
      setActiveChat(existing);
      return existing;
    }
    
    const otherUserDoc = await getDoc(doc(db, 'users', otherUserId));
    const otherUserName = otherUserDoc.exists()
      ? (otherUserDoc.data().displayName || otherUserDoc.data().email || 'User')
      : 'User';
    
    const participants = [user.uid, otherUserId];
    const unreadCounts = {};
    participants.forEach(uid => { unreadCounts[uid] = 0; });
    
    const chatData = {
      type: 'private',
      participants,
      name: otherUserName,
      unreadCounts,
      lastUpdated: serverTimestamp(),
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'chats'), chatData);
    const newChat = { id: docRef.id, ...chatData };
    setChats(prev => [newChat, ...prev]);
    setActiveChat(newChat);
    return newChat;
  };
  
  const createGroupChat = async (groupName, selectedUserIds, groupPhotoUrl = null) => {
    if (!user) return null;
    if (!groupName.trim() || selectedUserIds.length === 0) return;
    
    const participants = [...selectedUserIds, user.uid];
    const unreadCounts = {};
    participants.forEach(uid => { unreadCounts[uid] = 0; });
    
    const chatData = {
      type: 'group',
      participants,
      name: groupName.trim(),
      groupPhoto: groupPhotoUrl || null,
      createdBy: user.uid,
      unreadCounts,
      lastUpdated: serverTimestamp(),
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'chats'), chatData);
    const newChat = { id: docRef.id, ...chatData };
    setChats(prev => [newChat, ...prev]);
    setActiveChat(newChat);
    return newChat;
  };
  
  // ===== DELETE CHAT =====
  const deleteChat = async (chatId) => {
    if (!user) return;
    if (!window.confirm('Delete this entire conversation? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'chats', chatId));
      setChats(prev => prev.filter(c => c.id !== chatId));
      if (activeChat?.id === chatId) setActiveChat(null);
    } catch (err) {
      console.error('Delete chat error:', err);
      alert('Failed to delete chat.');
    }
  };
  
  // ===== MARK CHAT AS READ =====
  const markChatAsRead = async (chatId) => {
    if (!user) return;
    const chatRef = doc(db, 'chats', chatId);
    await updateDoc(chatRef, { [`unreadCounts.${user.uid}`]: 0 });
  };
  
  // ===== EDIT MESSAGE =====
  const editMessage = async (chatId, messageId, newText) => {
    if (!user || !newText.trim()) return;
    const msgRef = doc(db, 'chats', chatId, 'messages', messageId);
    await updateDoc(msgRef, {
      text: newText.trim(),
      edited: true,
      editedAt: serverTimestamp()
    });
  };
  
  // ===== DELETE MESSAGE =====
  const deleteMessage = async (chatId, messageId) => {
    if (!user) return;
    if (!window.confirm('Delete this message?')) return;
    const msgRef = doc(db, 'chats', chatId, 'messages', messageId);
    await updateDoc(msgRef, {
      text: 'This message was deleted',
      deleted: true,
      deletedAt: serverTimestamp()
    });
  };
  
  // ===== ADD REACTION =====
  const addReaction = async (chatId, messageId, reactionType) => {
    if (!user) return;
    const msgRef = doc(db, 'chats', chatId, 'messages', messageId);
    const msgSnap = await getDoc(msgRef);
    if (!msgSnap.exists()) return;
    const currentReactions = msgSnap.data().reactions || {};
    if (currentReactions[user.uid] === reactionType) {
      delete currentReactions[user.uid];
    } else {
      currentReactions[user.uid] = reactionType;
    }
    await updateDoc(msgRef, { reactions: currentReactions });
  };
  
  // ===== ADD GROUP MEMBERS =====
  const addGroupMembers = async (chatId, newUserIds) => {
    if (!user) return;
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) return;
    const currentParticipants = chatSnap.data().participants || [];
    const newParticipants = [...new Set([...currentParticipants, ...newUserIds])];
    const unreadCounts = { ...(chatSnap.data().unreadCounts || {}) };
    newUserIds.forEach(uid => {
      if (!currentParticipants.includes(uid)) {
        unreadCounts[uid] = 0;
      }
    });
    await updateDoc(chatRef, {
      participants: newParticipants,
      unreadCounts: unreadCounts
    });
  };
  
  // ===== LISTEN TO CHATS =====
  useEffect(() => {
    if (!user) {
      setChats([]);
      setLoading(false);
      return;
    }
    
    const q = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', user.uid),
      orderBy('lastUpdated', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatData = [];
      const seenIds = new Set();
      
      snapshot.forEach(doc => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id);
          chatData.push({ id: doc.id, ...doc.data() });
        }
      });
      
      setChats(chatData);
      setLoading(false);
    }, err => console.error("Chats error:", err));
    
    return () => unsubscribe();
  }, [user]);
  
  // ===== LISTEN TO MESSAGES =====
  useEffect(() => {
    if (!activeChat || !user) {
      setMessages([]);
      return;
    }
    
    setMessagesLoading(true);
    const messagesRef = collection(db, 'chats', activeChat.id, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!isMounted.current) return;
      
      const msgs = [];
      snapshot.forEach(docSnap => {
        msgs.push({ id: docSnap.id, ...docSnap.data() });
      });
      
      setMessages(msgs);
      setMessagesLoading(false);
      
      const unreadMsgs = msgs.filter(m => 
        m.senderId !== user.uid && (!m.readBy || !m.readBy.includes(user.uid))
      );
      
      if (unreadMsgs.length > 0) {
        unreadMsgs.forEach(msg => {
          const msgRef = doc(db, 'chats', activeChat.id, 'messages', msg.id);
          updateDoc(msgRef, { readBy: arrayUnion(user.uid) }).catch(console.error);
        });
        
        const chatRef = doc(db, 'chats', activeChat.id);
        updateDoc(chatRef, { [`unreadCounts.${user.uid}`]: 0 }).catch(console.error);
      }
    }, err => console.error("Messages error:", err));
    
    return () => unsubscribe();
  }, [activeChat, user]);
  
  // ===== ONLINE STATUS =====
  useEffect(() => {
    if (!user) return;
    
    const userStatusRef = doc(db, 'status', user.uid);
    setDoc(userStatusRef, { state: 'online', lastChanged: serverTimestamp() }).catch(console.error);
    
    return () => {
      setDoc(userStatusRef, { state: 'offline', lastChanged: serverTimestamp() }).catch(console.error);
    };
  }, [user]);
  
  // Subscribe to online status of chat participants
  useEffect(() => {
    if (!user) {
      setOnlineUsers({});
      return;
    }
    
    const getRelevantUids = () => {
      const uids = new Set();
      uids.add(user.uid);
      chats.forEach(chat => {
        chat.participants?.forEach(uid => uids.add(uid));
      });
      usersList.forEach(u => uids.add(u.uid));
      return uids;
    };
    
    const unsubscribeMap = new Map();
    
    const updateStatusSubscriptions = () => {
      const relevantUids = getRelevantUids();
      
      for (const [uid, unsub] of unsubscribeMap.entries()) {
        if (!relevantUids.has(uid)) {
          unsub();
          unsubscribeMap.delete(uid);
          setOnlineUsers(prev => {
            const newState = { ...prev };
            delete newState[uid];
            return newState;
          });
        }
      }
      
      relevantUids.forEach(uid => {
        if (uid !== user.uid && !unsubscribeMap.has(uid)) {
          const statusRef = doc(db, 'status', uid);
          const unsubscribe = onSnapshot(statusRef, (docSnap) => {
            const isOnline = docSnap.exists() && docSnap.data().state === 'online';
            setOnlineUsers(prev => ({ ...prev, [uid]: isOnline }));
          }, (err) => console.warn(`Status error for ${uid}:`, err));
          unsubscribeMap.set(uid, unsubscribe);
        }
      });
    };
    
    updateStatusSubscriptions();
    
    return () => {
      unsubscribeMap.forEach(unsub => unsub());
    };
  }, [chats, usersList, user]);
  
  // ===== TYPING INDICATORS LISTENER =====
  useEffect(() => {
    if (!activeChat) {
      setTypingUsers([]);
      return;
    }
    
    const typingCol = collection(db, 'chats', activeChat.id, 'typing');
    const threeSecAgo = new Date(Date.now() - 3000);
    const q = query(typingCol, where('timestamp', '>=', threeSecAgo));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const typists = [];
      snapshot.forEach(doc => typists.push(doc.id));
      setTypingUsers(typists.filter(uid => uid !== user?.uid));
    }, err => console.error("Typing error:", err));
    
    return () => unsubscribe();
  }, [activeChat, user]);
  
  // ===== FETCH USERS =====
  useEffect(() => {
    const fetchUsers = async () => {
      if (!user) return;
      try {
        const usersRef = collection(db, 'users');
        const snap = await getDocs(usersRef);
        const users = snap.docs
          .map(doc => ({ uid: doc.id, ...doc.data() }))
          .filter(u => u.isBlocked !== true && u.uid !== user.uid);
        setUsersList(users);
      } catch (err) {
        console.error("Users fetch error:", err);
        setUsersList([]);
      }
    };
    fetchUsers();
  }, [user]);
  
  // ===== UNREAD COUNT =====
  useEffect(() => {
    if (!user) {
      setTotalUnreadCount(0);
      return;
    }
    let sum = 0;
    chats.forEach(chat => {
      sum += chat.unreadCounts?.[user.uid] || 0;
    });
    setTotalUnreadCount(sum);
  }, [chats, user]);
  
  const getUserOnlineStatus = (uid) => onlineUsers[uid] === true;
  
  const value = {
    chats,
    loading,
    activeChat,
    setActiveChat,
    messages,
    messagesLoading,
    sendMessage,
    sendImage,
    startTyping,
    stopTyping,
    typingUsers,
    createPrivateChat,
    createGroupChat,
    usersList,
    deleteChat,
    editMessage,
    deleteMessage,
    addReaction,
    addGroupMembers,
    getUserOnlineStatus,
    totalUnreadCount,
    markChatAsRead,
  };
  
  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};